import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS } from '../../constants';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import { formatCurrency, getEffectivePrice } from '../../utils/pricing';
import { resolveImageUrl } from '../../utils/image';
import { Product } from '../../types';

interface AbandonedCart {
  _id: string;
  updatedAt: string;
  user?: { _id: string; name: string; email: string; phone?: string } | null;
  items: Array<{ product: Product | null; quantity: number }>;
}

const relativeTime = (iso: string): string => {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)} days ago`;
};

const AdminAbandonedCartsPage: React.FC = () => {
  const [carts, setCarts] = useState<AbandonedCart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get(`${API_ENDPOINTS.CART}/abandoned`)
      .then((res) => setCarts(res.data as AbandonedCart[]))
      .catch((err) => setError(extractApiErrorMessage(err, 'Failed to load abandoned carts')))
      .finally(() => setLoading(false));
  }, []);

  const cartValue = (cart: AbandonedCart) =>
    cart.items.reduce(
      (sum, item) => sum + (item.product ? getEffectivePrice(item.product) * item.quantity : 0),
      0
    );

  const totalValue = carts.reduce((sum, cart) => sum + cartValue(cart), 0);

  const followUpLink = (cart: AbandonedCart) => {
    const first = cart.items.find((i) => i.product)?.product;
    const subject = encodeURIComponent('Still thinking it over? Your Vediluxe cart is waiting ✨');
    const body = encodeURIComponent(
      `Hi ${cart.user?.name?.split(' ')[0] || 'there'},\n\n` +
        `We noticed you left ${first ? `"${first.name}"` : 'a few pieces'} in your cart. ` +
        `They're still available — you can complete your order here:\n\n` +
        `https://vediluxe.com/cart\n\n` +
        `If you have any questions, just reply to this email.\n\nWarmly,\nTeam Vediluxe`
    );
    return `mailto:${cart.user?.email || ''}?subject=${subject}&body=${body}`;
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Abandoned carts
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Carts with items, untouched for 24 hours or more
          {carts.length > 0 && ` · ${formatCurrency(totalValue)} in potential revenue`}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Items</TableCell>
              <TableCell align="right">Cart value</TableCell>
              <TableCell>Last activity</TableCell>
              <TableCell align="right">Follow up</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(5)].map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : carts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 5 }}>
                  <Typography color="text.secondary">
                    No abandoned carts right now — everyone's checking out. ✨
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              carts.map((cart) => (
                <TableRow key={cart._id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {cart.user?.name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cart.user?.email}
                      {cart.user?.phone ? ` · ${cart.user.phone}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {cart.items.slice(0, 4).map((item, i) =>
                        item.product ? (
                          <Tooltip key={i} title={`${item.product.name} × ${item.quantity}`}>
                            <Box
                              component="img"
                              src={resolveImageUrl(item.product.images?.[0] || '')}
                              alt=""
                              sx={{ width: 30, height: 30, borderRadius: 1, objectFit: 'cover' }}
                            />
                          </Tooltip>
                        ) : null
                      )}
                      {cart.items.length > 4 && (
                        <Typography variant="caption" color="text.secondary">
                          +{cart.items.length - 4}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(cartValue(cart))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={relativeTime(cart.updatedAt)} />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<MailOutlineIcon fontSize="small" />}
                      href={followUpLink(cart)}
                      disabled={!cart.user?.email}
                    >
                      Email
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default AdminAbandonedCartsPage;
